import encodeWebp from '@jsquash/webp/encode';

const createImage = (url) =>
  new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener('load', () => resolve(image));
    image.addEventListener('error', (error) => reject(error));
    image.src = url;
  });

const canvasToBlob = (canvas, type, quality) =>
  new Promise((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('Failed to export image'));
      },
      type,
      quality,
    );
  });

// Bakes a 90°-step rotation into a new image so the crop UI always works on an
// upright, correctly-sized image - no separate rotation math to keep in sync
// with the crop rectangle.
export const rotateImage90 = async (imageSrc, direction) => {
  const image = await createImage(imageSrc);
  const canvas = document.createElement('canvas');
  canvas.width = image.height;
  canvas.height = image.width;

  const ctx = canvas.getContext('2d');
  ctx.translate(canvas.width / 2, canvas.height / 2);
  ctx.rotate((direction === 'cw' ? 90 : -90) * (Math.PI / 180));
  ctx.drawImage(image, -image.width / 2, -image.height / 2);

  const blob = await canvasToBlob(canvas, 'image/png', 1);
  return URL.createObjectURL(blob);
};

// Crops a freeform pixel rect (from react-image-crop) and exports a webp Blob.
// Safari/WebKit (incl. iOS Chrome, which embeds WebKit) doesn't support encoding
// to image/webp via canvas.toBlob on most versions, so we encode with jSquash's
// WASM webp encoder instead — that works identically on every browser, iOS
// included. canvas.toBlob is kept only as a last-resort fallback if the WASM
// encoder itself fails (e.g. blocked by a strict CSP).
export const getCroppedWebpBlob = async (imageSrc, pixelCrop, quality = 0.85) => {
  const image = await createImage(imageSrc);

  const canvas = document.createElement('canvas');
  canvas.width = pixelCrop.width;
  canvas.height = pixelCrop.height;
  const ctx = canvas.getContext('2d');

  ctx.drawImage(
    image,
    pixelCrop.x,
    pixelCrop.y,
    pixelCrop.width,
    pixelCrop.height,
    0,
    0,
    pixelCrop.width,
    pixelCrop.height,
  );

  try {
    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    const webpBuffer = await encodeWebp(imageData, { quality: Math.round(quality * 100) });
    return new Blob([webpBuffer], { type: 'image/webp' });
  } catch (error) {
    return canvasToBlob(canvas, 'image/webp', quality);
  }
};

export const getFileExtensionForBlob = (blob) => {
  if (blob.type === 'image/webp') return 'webp';
  if (blob.type === 'image/jpeg') return 'jpg';
  return 'png';
};
